import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Tesla Model S (original, ~2016) — the long, low, sleek electric fastback
// saloon at true scale (1 unit = 1 metre): length 4.98, width 1.96, height 1.44,
// wheelbase 2.96, on ~0.36 m aero alloys. Nose is +Z. Authored for the `cars`
// showcase batch. The recognisability cues: a very LOW and WIDE sedan with a
// fastback / liftback roof — ONE continuous descending arc from the raked
// windscreen over a low cab-forward roof sweeping all the way down to the tail,
// with NO flat rear deck / step (it is a liftback, not a notchback); a long
// wheelbase with short overhangs; the grille-less smooth body-colour nose (it is
// electric — no grille, just a small black lower intake) tapering to a low nose
// cone, with slim swept-back wrap-around LED headlamps and a small chrome "T"
// hint; a big glass greenhouse with a chrome window surround and flush door
// handles; a panoramic dark glass roof panel; a smooth liftback tail with slim
// wrap-around red tail lamps joined into a connected light bar, a subtle lip
// spoiler and a smooth lower diffuser with NO exhaust pipes (electric); clean
// multi-spoke silver aero alloys sitting flush in tight arches. Body panels
// carry the "paint" recolour slot (iconic Tesla Red Multi-Coat); `drive` spins
// the wheels and `hazards` blinks six indicators.

const PAINT = "#b31527"; // Tesla Red Multi-Coat — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, lower intake, diffuser)
const CLAD = "#26282c"; // dark lower bumpers / valances
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2831"; // tinted side / rear glazing
const SCREEN = "#2b3e4e"; // windscreen (a shade lighter than the tint)
const PANO = "#10151b"; // near-black panoramic glass roof panel
const CHROME = "#ccd1d6"; // chrome window surround, badge, trim
const SILVER = "#b4b9c0"; // aero alloy faces
const DARKSIL = "#3a3d42"; // spoke grooves / wheel dish shadow
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const LED = "#f6fbff"; // LED signature strips
const AMBER = "#e8a13a"; // indicators
const RED = "#e01a2b"; // tail lamps — bright red so they read as the light bar
const REDDARK = "#7a0f1c"; // deep-red reflector
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Long wheelbase, short overhangs (cab-forward).
// Kept deliberately LOW and WIDE: beltline 0.86, roof peak 1.35, body 1.88 wide
// with the track a touch wider, so the front reads clearly wider than tall.
const WHEEL_R = 0.36;
const TYRE_W = 0.25;
const WHEEL_X = 0.88; // half track — tyre outer face proud of the body side (outer cap ~1.0)
const AXLE_F = 1.5;
const AXLE_R = -1.46; // wheelbase 2.96
const BODY_X = 0.94; // main body half-width (1.88 wide)
const BELT = 0.86; // beltline / shoulder height — kept low

// The unbroken fastback arc: four thin tilted paint boxes (thin in y, long in z)
// whose top surfaces meet on the same descending breakpoints and OVERLAP the
// neighbour — one smooth curve from the windscreen header over a low cab-forward
// roof down to the tail, with NO flat deck. Breakpoints (z, y_top):
//   (0.5, 1.36) roof front -> (-0.4, 1.34) low roof -> (-1.15, 1.18) upper
//   backlight -> (-1.85, 1.02) lower backlight -> (-2.25, 0.95) tail lip.
const SCREEN_RAKE = 0.805; // strongly raked, cab-forward windscreen
const ROOF_A = 0.025; // low roof, barely sloped
const BL1_A = 0.186; // upper backlight
const BL2_A = 0.226; // lower backlight (steepest)
const TAIL_A = 0.197; // tail lip flattening onto the trailing edge

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the smooth grille-less nose cone. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One clean multi-spoke silver aero alloy: tyre, brake disc, dark recessed
 *  dish, a proud silver aero face with five dark spoke grooves, chrome centre
 *  cap. Every part shares `name` so the `drive` track spins them as one node
 *  about the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.16, 0.16, 0.03, 16, BRAKE, [x - side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.26, 0.26, 0.06, 20, "#202225", [x + side * 0.065, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.3, 0.3, 0.03, 24, SILVER, [x + side * 0.095, y, z], [0, 0, Math.PI / 2], { name }), // aero face
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.03, 0.56, 0.05, DARKSIL, [x + side * 0.105, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.03, 14, CHROME, [x + side * 0.115, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus a slim
 *  body-colour brow (an upper half-torus over the tyre — the Model S has clean
 *  body-colour flush arches, no black cladding). The [0, pi/2, 0] yaw puts the
 *  0..pi arc exactly over the top half; the tyre's outer cap stays proud of both
 *  so the wheel is never swallowed. Kept tight so the tyre nearly fills it. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.45, 0.45, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.44, 0.05, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The six hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair are amber chips in the headlamps, rear pair are amber chips under
// the tail lamps, and a side pair sit on the front fenders (repeaters).
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.64, 0.74, 2.47], size: [0.16, 0.04, 0.03] },
  { name: "indicator-fr", pos: [0.64, 0.74, 2.47], size: [0.16, 0.04, 0.03] },
  { name: "indicator-rl", pos: [-0.66, 0.66, -2.48], size: [0.12, 0.05, 0.03] },
  { name: "indicator-rr", pos: [0.66, 0.66, -2.48], size: [0.12, 0.05, 0.03] },
  { name: "indicator-ml", pos: [-0.95, 0.8, 1.1], size: [0.02, 0.03, 0.06] },
  { name: "indicator-mr", pos: [0.95, 0.8, 1.1], size: [0.02, 0.03, 0.06] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.7, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  // Low battery-pack floor (the electric "skateboard"): a wide low pan.
  P.push(box(1.8, 0.16, 4.3, TRIM, [0, 0.28, -0.03]));

  // -- Body masses (paint) — low and wide ------------------------------------
  P.push(paint(1.88, 0.64, 4.3, [0, 0.54, -0.03])); // main tub, sill to shoulder (top at 0.86)
  P.push(paint(1.86, 0.08, 3.6, [0, BELT, -0.35])); // shoulder / belt-line band
  // Long low bonnet, front lower toward the smooth nose (short front overhang).
  P.push(paint(1.6, 0.09, 1.5, [0, 0.84, 1.6], [0.1, 0, 0]));
  for (const s of S) P.push(paint(0.14, 0.09, 1.5, [s * 0.87, 0.86, 1.6], [0.1, 0, 0])); // fender tops
  // Smooth grille-less nose tapering to a low, clean nose cone.
  P.push(paint(1.78, 0.28, 0.22, [0, 0.63, 2.36]));
  P.push(blob([0.9, 0.15, 0.13], [0, 0.66, 2.44]));
  // Tail: a body-colour panel the descending liftback lands onto.
  P.push(paint(1.86, 0.46, 0.26, [0, 0.63, -2.34]));

  // -- The unbroken fastback arc: low roof sweeping down to the tail ----------
  // Four thin tilted boxes forming one continuous descending line — NO flat
  // deck. Each is a little longer than its span so ends overlap the neighbour.
  P.push(paint(1.44, 0.07, 0.98, [0, 1.315, 0.05], [-ROOF_A, 0, 0])); // low cab-forward roof
  P.push(box(1.2, 0.03, 0.86, PANO, [0, 1.352, 0.05], [-ROOF_A, 0, 0])); // panoramic glass roof, barely proud
  P.push(paint(1.4, 0.1, 0.92, [0, 1.205, -0.75], [-BL1_A, 0, 0])); // upper backlight sail
  P.push(paint(1.44, 0.12, 0.84, [0, 1.05, -1.5], [-BL2_A, 0, 0])); // lower backlight onto the tail
  P.push(paint(1.46, 0.1, 0.52, [0, 0.945, -2.03], [-TAIL_A, 0, 0])); // tail lip surface
  P.push(paint(1.4, 0.05, 0.16, [0, 1.0, -2.08])); // subtle lip spoiler at the trailing edge

  // -- Greenhouse -------------------------------------------------------------
  P.push(box(1.42, 0.03, 0.1, TRIM, [0, 0.9, 1.0])); // cowl vent under the windscreen
  P.push(box(1.42, 0.68, 0.05, SCREEN, [0, 1.1, 0.7], [-SCREEN_RAKE, 0, 0])); // raked windscreen
  for (const s of S) P.push(paint(0.1, 0.7, 0.09, [s * 0.68, 1.1, 0.7], [-SCREEN_RAKE, 0, 0])); // A-pillars
  for (const s of S) {
    // Side glass band (the long 4-door DLO), inboard of the body so it never
    // splays past the roof as "ears"; small tumblehome. Its flat top stays
    // under the low roof so it never pokes above the descending roofline.
    P.push(box(0.035, 0.4, 1.25, GLASS, [s * 0.68, 1.06, -0.125], [0, 0, s * 0.07]));
    P.push(box(0.045, 0.42, 0.06, TRIM, [s * 0.685, 1.06, 0.1], [0, 0, s * 0.07])); // B-pillar
    P.push(box(0.045, 0.4, 0.05, TRIM, [s * 0.685, 1.06, -0.5], [0, 0, s * 0.07])); // rear-door divider
    P.push(box(0.02, 0.03, 1.35, CHROME, [s * 0.885, BELT, -0.125])); // chrome window surround / belt strip
  }
  P.push(box(1.12, 0.03, 0.82, GLASS, [0, 1.255, -0.75], [-BL1_A, 0, 0])); // liftback backlight, upper
  P.push(box(1.16, 0.03, 0.56, GLASS, [0, 1.12, -1.35], [-BL2_A, 0, 0])); // liftback backlight, lower
  P.push(box(0.05, 0.14, 0.18, TRIM, [0, 1.4, -0.25])); // shark-fin antenna, sunk into the roof

  // -- Front fascia: grille-less smooth nose, slim wrap-around lamps -----------
  // No grille (it is electric) — only slim swept LED headlamps that wrap the
  // corners, a small black lower intake and a chrome "T" hint on the nose.
  for (const s of S) {
    P.push(box(0.46, 0.1, 0.06, LAMP, [s * 0.62, 0.74, 2.43])); // slim swept headlamp
    P.push(box(0.5, 0.03, 0.03, LED, [s * 0.62, 0.79, 2.46])); // LED signature strip on top
    P.push(box(0.1, 0.09, 0.16, LAMP, [s * 0.86, 0.72, 2.33])); // wrap-around corner
  }
  P.push(box(0.03, 0.1, 0.03, CHROME, [0, 0.71, 2.48])); // "T" badge — vertical stem
  P.push(box(0.12, 0.03, 0.03, CHROME, [0, 0.755, 2.48])); // "T" badge — top bar
  P.push(box(0.9, 0.11, 0.06, TRIM, [0, 0.42, 2.46])); // small black lower intake
  P.push(box(1.74, 0.24, 0.34, CLAD, [0, 0.38, 2.29])); // front lower bumper, back to the arch
  P.push(box(0.5, 0.11, 0.02, PLATE, [0, 0.56, 2.47])); // front plate

  // -- Rear fascia: slim wrap-around tail lamps joined into a light bar --------
  for (const s of S) {
    P.push(box(0.5, 0.17, 0.03, TRIM, [s * 0.52, 0.81, -2.465])); // dark lamp housing
    P.push(box(0.46, 0.15, 0.05, RED, [s * 0.52, 0.81, -2.48])); // tail lamp
    P.push(box(0.05, 0.15, 0.28, RED, [s * 0.945, 0.81, -2.36])); // corner wrap onto the flank
  }
  P.push(box(1.12, 0.08, 0.04, RED, [0, 0.81, -2.48])); // connecting light bar across the tail
  P.push(box(0.5, 0.02, 0.02, RED, [0, 1.0, -2.03])); // high-mount brake light on the spoiler
  for (const s of S) P.push(box(0.09, 0.04, 0.03, REDDARK, [s * 0.72, 0.46, -2.47])); // low reflectors
  P.push(box(1.1, 0.13, 0.06, TRIM, [0, 0.4, -2.47])); // smooth lower diffuser (NO exhausts — electric)
  for (let i = -2; i <= 2; i++) P.push(box(0.02, 0.11, 0.02, "#0e0f11", [i * 0.18, 0.4, -2.5])); // diffuser fins
  P.push(box(1.74, 0.24, 0.34, CLAD, [0, 0.38, -2.29])); // rear lower bumper, back to the arch
  P.push(box(0.5, 0.11, 0.02, PLATE, [0, 0.56, -2.47])); // rear plate

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.06, 0.03, 0.04, TRIM, [s * 0.95, 0.92, 0.55])); // mirror arm
    P.push(paint(0.1, 0.09, 0.13, [s * 1.0, 0.94, 0.53])); // mirror cap
    P.push(box(0.09, 0.07, 0.012, CHROME, [s * 1.048, 0.94, 0.46])); // mirror glass
    // Flush door handles — thin chrome bars flush with the flank, one per door.
    P.push(box(0.02, 0.03, 0.15, CHROME, [s * 0.95, 0.8, 0.35]));
    P.push(box(0.02, 0.03, 0.15, CHROME, [s * 0.95, 0.8, -0.5]));
    P.push(box(0.28, 0.012, 0.03, TRIM, [s * 0.3, 0.9, 1.0], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const teslaModelS = defineModel({
  id: "tesla-model-s",
  name: "Tesla Model S",
  batchId: "cars",
  tags: ["car", "tesla", "model-s", "electric", "sedan", "fastback", "modern"],
  summary: "An original Tesla Model S electric fastback saloon at true scale, in Tesla Red Multi-Coat.",
  description:
    "An original Tesla Model S (~2016) electric fastback saloon at real " +
    "1 unit = 1 metre scale (about 4.98 long, 1.96 wide, 1.44 tall, 2.96 " +
    "wheelbase, on clean multi-spoke silver aero alloys). The silhouette is a " +
    "long, low, wide sedan with a fastback / liftback roof: one continuous " +
    "descending arc from the raked cab-forward windscreen over a low panoramic-" +
    "glass roof sweeping all the way down to the tail, with no flat deck or " +
    "step. The signature cues are all present: a grille-less smooth body-colour " +
    "nose (it is electric) tapering to a low nose cone, with a small black lower " +
    "intake, slim swept-back wrap-around LED headlamps and a chrome 'T' hint; a " +
    "big glass greenhouse with a chrome window surround and flush door handles; " +
    "a smooth liftback tail with slim wrap-around red tail lamps joined into a " +
    "connected light bar, a subtle lip spoiler and a smooth lower diffuser with " +
    "no exhaust pipes. Body panels carry the 'paint' recolour slot (Tesla Red " +
    "Multi-Coat); `drive` spins the wheels and `hazards` blinks the six " +
    "indicators.",
  author: "Fable 5",
  footprint: 2.64,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
